import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter, forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output, SimpleChanges,
  ViewChild
} from '@angular/core';
import CooperationModel from '../../../../model/cooperation.model';
import UserModel from '../../../../model/user.model';
import {BehaviorSubject, forkJoin, of, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, switchMap} from 'rxjs/operators';
import CooperationService from '../../../../api/cooperation/cooperation.service';
import UserService from '../../../../api/user/user.service';
import NoteModel from '../../../../model/note.model';
import CustomerService from '../../../../api/customer/customer.service';
import {CustomerFormComponent, CustomerFormDialogModel} from '../../customer/customer-form/customer-form.component';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import * as moment from 'moment';
import CustomerModel from '../../../../model/customer.model';
import DoingModel from "../../../../model/doing.model";
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from "@angular/forms";

@Component({
  selector: 'app-note-form',
  templateUrl: './note-form.component.html',
  styleUrls: ['./note-form.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => NoteFormComponent),
    multi: true
  }]
})
export class NoteFormComponent implements OnInit, AfterViewInit, ControlValueAccessor, OnChanges {
  public note: NoteModel;
  public disabled = false;
  public searchOpened = false;

  private positionSubject = new BehaviorSubject<'top'|'bottom'>('bottom');
  public position$ = this.positionSubject.asObservable();

  private querySubject = new BehaviorSubject<string>('');
  public query$ = this.querySubject.asObservable();


  private resultsSubject = new BehaviorSubject<QueryResult[]>([]);
  public results$ = this.resultsSubject.asObservable();

  private focusSubject = new Subject();
  public focus$ = this.focusSubject.asObservable();

  public selectedIndex = 0;

  @Input() cooperationSearchEnabled = true;
  @Input() userSearchEnabled = true;
  @Input() customerSearchEnabled = true;

  @Input() autofocus = false;

  @Input() tagTrigger = '#';
  @Input() placeholderText = 'Notiz hinzufügen';
  @Input() defaultFollowUpDate = null;

  @Output() noteSubmitted = new EventEmitter<NoteModel>();

  @ViewChild('noteInput') noteInput: ElementRef;
  @ViewChild('searchQueryInput') searchQueryInput: ElementRef;
  @ViewChild('searchMenuTrigger', { read: ElementRef }) searchMenuTrigger: ElementRef;

  public multilineMode = true;

  private onChange: any = () => { };
  private onTouched: any = () => { };

  constructor(private cooperationService: CooperationService,
              private dialog: MatDialog,
              private userService: UserService,
              private el: ElementRef,
              private snackBar: MatSnackBar,
              private customerService: CustomerService) {

  }

  writeValue(obj: any): void {
    if(obj) {
      this.note = obj;
    } else {
      this.note = {
        text: '',
      };
    }
  }

  saveDoing() {
    this.noteSubmitted.emit(this.note)
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngOnInit(): void {
    if(!this.note) {
      this.note = {
        text: ''
      };
    }

    this.focus$.pipe(
        debounceTime(200),
        distinctUntilChanged()
    ).subscribe(focus => {
      if (!focus) {
        this.focusLost();
      }
    });

    this.query$.pipe(
        debounceTime(200),
        distinctUntilChanged()
    ).subscribe(query => {
      if (!this.searchOpened) { return; }
      const requests = [];

      if (this.userSearchEnabled) {
        requests.push(this.userService.list(query, 1, 3)
            .pipe(switchMap(result => {
              const resultList = [] as QueryResult[];
              for (const user of result.results) {
                resultList.push({
                  id: user.id,
                  originalObject: user,
                  label: user.displayName ? user.displayName : user.username,
                  type: 'user',
                });
              }
              return of(resultList);
            })));
      }

      if (this.customerSearchEnabled) {
        requests.push(this.customerService.list(query, 1, 15, null, null, { state: 'customer' })
            .pipe(switchMap(result => {
              const resultList = [] as QueryResult[];
              for (const customer of result.results) {
                resultList.push({
                  id: customer.id,
                  label: customer.companyDisplayName ? customer.companyDisplayName : customer.companyName,
                  type: 'customer',
                  originalObject: customer,
                  image: customer.logo ? customer.logo.filename : null
                });
              }
              return of(resultList);
            })));
      }

      if (this.cooperationSearchEnabled) {
        let cooperationAdditionalParameters = {};
        if(this.note.user) {
          cooperationAdditionalParameters = {
            user: this.note.user.id
          };
        }
        requests.push(
            this.cooperationService.list(query, 1, 15, 'created', 'desc',  cooperationAdditionalParameters)
                .pipe(switchMap(result => {
                  const resultList = [] as QueryResult[];
                  for (const coop of result.results) {
                    const dateframe = [];
                    if(coop.earliestEvent) {
                      dateframe.push(moment(coop.earliestEvent).format('DD.MM.YYYY'));
                    }
                    if(coop.latestEvent) {
                      dateframe.push(moment(coop.latestEvent).format('DD.MM.YYYY'));
                    }
                    resultList.push({
                      id: coop.id,
                      originalObject: coop,
                      label: coop.name,
                      image: coop.customer.logo ? coop.customer.logo.filename : null,
                      type: 'cooperation',
                      description: dateframe.join(' - ')
                    });
                  }
                  return of(resultList);
                }))
        );
      }

      forkJoin(requests).subscribe(results => {
        let combinedResults = [];
        for (const result of results) {
          combinedResults = combinedResults.concat(result);
        }
        this.resultsSubject.next(combinedResults);
      });
    });
  }

  keyDown(event) {
    event.stopPropagation();
    console.log(event.key);
    if (event.key === this.tagTrigger && !this.searchOpened ) {
      event.preventDefault();
      this.searchOpened = true;
      this.searchMenuTrigger.nativeElement.click();
      this.querySubject.next('');
      this.selectedIndex = 0;
      setTimeout(() => {
        this.searchQueryInput.nativeElement.focus();
      }, 10);
    }

    if (event.key === 'Enter' && (!this.multilineMode || event.ctrlKey) && this.noteInput.nativeElement.value != '') {
      this.noteSubmitted.emit(this.note);
      this.note = {
        text: ''
      };
      this.noteInput.nativeElement.value = '';
    }
  }

  ngOnChanges(changes: SimpleChanges) {

  }

  ngAfterViewInit() {
    this.results$.subscribe(results => {
      if(this.positionSubject.getValue() == 'top') {
        this.selectedIndex = results.length - 1;
      } else {
        this.selectedIndex = 0;
      }
    });
    window.setInterval(() => {
      let rect = this.el.nativeElement.getBoundingClientRect();
      let remainingSpaceBelow = window.innerHeight - rect.top - rect.height;
      if(remainingSpaceBelow < 300) {
        this.positionSubject.next('top');
      } else {
        this.positionSubject.next('bottom');
      }
    }, 2000);

    if(this.autofocus) {
      setTimeout(() => {
        this.noteInput.nativeElement.focus();
      }, 500);
    }
  }

  focusLost() {
    this.searchOpened = false;
  }

  backspacePressed() {
    if(this.querySubject.getValue() == '') {
      this.searchOpened = false;
      this.searchMenuTrigger.nativeElement.click();
      setTimeout(() => {
        this.noteInput.nativeElement.focus();
      }, 10);
    }
  }

  escapePressed() {
    this.searchOpened = false;
    this.searchMenuTrigger.nativeElement.click();
    setTimeout(() => {
      this.noteInput.nativeElement.focus();
    }, 10);
  }

  arrowDownPressed() {
    if(this.positionSubject.getValue() == 'top') {
      if (this.selectedIndex  < this.resultsSubject.getValue().length - 1) {
        this.selectedIndex++;
      }
    } else {
      if (this.selectedIndex > 0) {
        this.selectedIndex--;
      }
    }
  }

  arrowUpPressed() {
    if(this.positionSubject.getValue() == 'top') {
      if (this.selectedIndex > 0) {
        this.selectedIndex--;
      }
    } else {
      if (this.selectedIndex  < this.resultsSubject.getValue().length - 1) {
        this.selectedIndex++;
      }
    }
  }

  matchInType(type: string) {
    for(let result of this.resultsSubject.getValue()) {
      if(result.type == type) {
        return true;
      }
    }
    return false;
  }

  setQuery(query) {
    this.querySubject.next(query);
  }

  addCustomer() {
    const dialogData = new CustomerFormDialogModel({
      companyName: this.querySubject.getValue(),
      companyDisplayName: this.querySubject.getValue(),
      socialMedia: [],
    } as CustomerModel);

    const dialogRef = this.dialog.open(CustomerFormComponent, {
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        this.customerService.create(dialogResult).subscribe(result => {
          this.snackBar.open('Unternehmen wurde hinzugefügt');
          this.note.customer = result;
          this.searchOpened = false;
        }, error => {
          this.snackBar.open('Fehler beim Hinzufügen des Unternehmens');
        });
      }
    });
  }

  saveSearch() {
    if (this.searchOpened) {
      const entries = this.resultsSubject.getValue();
      if (0 <= this.selectedIndex && this.selectedIndex < entries.length) {
        const entry = entries[this.selectedIndex];
        if (entry.type === 'customer') {
          this.note.customer = entry.originalObject;
        } else if (entry.type === 'user') {
          this.note.markedUser = entry.originalObject;
        } else if (entry.type === 'cooperation') {
          this.note.cooperation = entry.originalObject;
        }
      }
    }
    this.searchOpened = false;
  }

  public focus() {
    this.noteInput.nativeElement.focus();
  }

  menuClosed() {
    this.searchOpened = false;
    this.searchQueryInput.nativeElement.value = '';
    setTimeout(() => {
      this.focus();
    }, 20)
  }

  applyContext(context: QueryResult) {
    console.log('apply context', context);
    if(context.type == 'customer') {
      this.note.customer = context.originalObject;
    } else if(context.type == 'user') {
      this.note.markedUser = context.originalObject;
    } else if(context.type == 'cooperation') {
      this.note.cooperation = context.originalObject;
    }
  }
}

interface QueryResult {
  id: number;
  type: 'customer'|'cooperation'|'user';
  label: string;
  description?: string;
  image?: string;
  originalObject: any;
}
